"use client";

/**
 * Client-side connection tracker combining browser connectivity
 * (navigator.onLine) with app-server reachability.
 *
 * The server side matters because a stopped/unreachable server makes every
 * API call fail while `navigator.onLine` stays true - which used to be
 * misread as "anonymous free user" and popped onboarding UI. Reachability is
 * verified with a ping to /api/health and re-checked every few seconds while
 * down, so consumers recover automatically once the server is back.
 *
 * Detection is passive: nothing polls while the connection is healthy.
 * Callers report suspected failures via reportServerUnreachable(), which
 * verifies with a ping before flipping state.
 */

const POLL_WHILE_DOWN_MS = 5000;
const PING_TIMEOUT_MS = 5000;

let online = true;
let serverReachable = true;
let pollTimer: ReturnType<typeof setTimeout> | null = null;
let pendingCheck: Promise<boolean> | null = null;
let windowListenersAttached = false;
const listeners = new Set<() => void>();

function emit() {
  for (const listener of Array.from(listeners)) listener();
}

function setState(nextOnline: boolean, nextServerReachable: boolean) {
  const changed = online !== nextOnline || serverReachable !== nextServerReachable;
  online = nextOnline;
  serverReachable = nextServerReachable;
  if (changed) emit();
  if (!getConnectionSnapshot()) schedulePoll();
}

function schedulePoll() {
  if (pollTimer !== null || typeof window === "undefined") return;
  pollTimer = setTimeout(() => {
    pollTimer = null;
    void checkServerReachable();
  }, POLL_WHILE_DOWN_MS);
}

function attachWindowListeners() {
  if (windowListenersAttached || typeof window === "undefined") return;
  windowListenersAttached = true;
  online = navigator.onLine;
  window.addEventListener("online", () => void checkServerReachable());
  window.addEventListener("offline", () => setState(false, serverReachable));
}

/**
 * Pings /api/health. Any HTTP response (even an error status) proves the
 * server answered; only a network-level failure marks it unreachable.
 */
export async function checkServerReachable(): Promise<boolean> {
  if (typeof window === "undefined") return true;
  if (!navigator.onLine) {
    setState(false, serverReachable);
    return false;
  }
  if (pendingCheck) return pendingCheck;
  pendingCheck = (async () => {
    try {
      await fetch("/api/health", {
        cache: "no-store",
        signal: AbortSignal.timeout(PING_TIMEOUT_MS),
      });
      setState(true, true);
      return true;
    } catch {
      setState(navigator.onLine, false);
      return false;
    } finally {
      pendingCheck = null;
    }
  })();
  return pendingCheck;
}

/** Call when a request to our own API got any HTTP response back. */
export function reportServerReachable() {
  setState(true, true);
}

/**
 * Call when a request to our own API failed at the network level.
 * Verifies with a ping before flipping state, then starts recovery polling.
 */
export function reportServerUnreachable() {
  void checkServerReachable();
}

export function getConnectionSnapshot(): boolean {
  return online && serverReachable;
}

export function subscribeConnection(listener: () => void): () => void {
  attachWindowListeners();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
